<template>
    <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
        <q-card style="width: 80vw; max-width: 1100px; border-radius: 8px;">
            <div class="flex full-width justify-between items-center q-py-sm q-pl-md q-pr-sm" style="border-bottom: 1px solid #dedede;">
                <div style="font-size: 12px; font-weight: 600;">Knowledge Guide</div>
                <q-btn flat icon="close" size="xs" color="grey-8" class="q-py-none q-px-xs" style="border-radius: 6px;" @click="$emit('update:modelValue', false)"/>
            </div>

            <q-tabs v-model="activeTab" dense no-caps active-color="accent" indicator-color="accent" align="left" class="full-width tabs" style="border-bottom: 1px solid #dedede;">
                <q-tab v-for="tab in tabs" :key="tab.value" :name="tab.value" :label="tab.label"/>
            </q-tabs>

            <q-scroll-area ref="guideScroll" class="q-pl-md q-pr-md" :thumb-style="thumbStyle" :horizontal-offset="[0, 4]" style="height: min(76vh, 700px); width: 100%;">
                <div class="guide q-py-md q-pr-sm">
                    <!-- APPLICATION USER GUIDE -->
                    <template v-if="activeTab === 'user-guide'">
                        <!-- HOW TO USE THE APPLICATION -->
                        <section class="guide-section">
                            <div class="section-header">How to Use the Application</div>
                            <p class="bodydg-text">
                                Follow the annotated screenshots below to move a business opportunity from discovery in the NBA Opportunity Hub,
                                through NBA generation and MLR approval in the NBA Agent Studio, to performance monitoring in the NBA Impact Center.
                                Click any screenshot to open it at full size.
                            </p>

                            <div v-for="module in walkthrough" :key="module.name" class="q-mt-md">
                                <div class="sub-header">{{ module.name }}</div>

                                <div v-for="step in module.steps" :key="step.image" class="source-card">
                                    <div class="source-title">Step {{ step.number }}: {{ step.title }}</div>
                                    <p class="bodydg-text">{{ step.description }}</p>
                                    <a :href="step.image" target="_blank" rel="noopener noreferrer">
                                        <img :src="step.image" :alt="`Step ${step.number}: ${step.title}`" class="screenshot" loading="lazy"/>
                                    </a>
                                </div>
                            </div>
                        </section>

                        <!-- GENERAL INSTRUCTIONS -->
                        <section class="guide-section">
                            <div class="section-header">General Instructions</div>
                            <p class="bodydg-text">
                                Getting started with Strategize, NBA Workbench, and Command Centre modules. Use this guide to understand how the
                                application behaves, how changes affect other users, and how to interpret the key terms and performance measures.
                            </p>

                            <div v-for="item in generalInstructions" :key="item.title" class="source-card">
                                <div class="source-title">{{ item.title }}</div>
                                <p v-for="paragraph in item.paragraphs" :key="paragraph" class="bodydg-text" v-html="paragraph"></p>
                            </div>
                        </section>

                        <!-- IMPORTANT DETAILS -->
                        <section class="guide-section">
                            <div class="section-header">Important Details</div>

                            <div class="source-card">
                                <div class="source-title">Your changes are visible to other users</div>
                                <p class="bodydg-text">The following changes on Strategize and NBA Workbench affect the shared application state:</p>
                                <ul class="bullet-list">
                                    <li v-for="change in sharedChanges" :key="change">{{ change }}</li>
                                </ul>
                                <p class="bodydg-text">
                                    Coordinate with other users before making these changes, especially during demos or parallel testing.
                                    Refresh the page before reviewing or changing a BCO to check its latest status.
                                </p>
                            </div>

                            <div class="source-card">
                                <div class="source-title">Data sources and the semantic layer</div>
                                <p class="bodydg-text">
                                    Refer to the <span class="term tab-link" @click="activeTab = 'agent-knowledge'">Agent Knowledge Guide</span> to understand the data sources and semantic layer
                                    used by the application. The semantic layer defines how data fields, business concepts, and metrics are interpreted.
                                </p>
                            </div>

                            <div class="source-card">
                                <div class="source-title">Acceptance and action are separate</div>
                                <p class="bodydg-text">
                                    An <span class="term">accepted</span> suggestion records a positive rep response. An <span class="term">actioned</span>
                                    suggestion has been accepted and subsequently acted on. Use Accept Rate and Action Rate separately when reviewing
                                    adoption and execution.
                                </p>
                            </div>
                        </section>

                        <!-- ACRONYMS -->
                        <section class="guide-section">
                            <div class="section-header">Acronyms</div>

                            <table class="metrics-table">
                                <thead>
                                    <tr>
                                        <th style="width: 22%;">Term</th>
                                        <th>Meaning</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="acronym in acronyms" :key="acronym.term">
                                        <td class="term">{{ acronym.term }}</td>
                                        <td>{{ acronym.meaning }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </section>

                        <!-- DEFINITIONS -->
                        <section class="guide-section">
                            <div class="section-header">Definitions</div>

                            <div class="source-card">
                                <div class="source-title">BCO categories</div>
                                <table class="metrics-table">
                                    <thead>
                                        <tr>
                                            <th style="width: 30%;">Category</th>
                                            <th>Definition</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="category in bcoCategories" :key="category.name">
                                            <td class="term">{{ category.name }}</td>
                                            <td>{{ category.definition }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="source-card">
                                <div class="source-title">Performance metrics</div>
                                <p class="bodydg-text">
                                    All rates below use total suggestions in the selected reporting scope as the denominator. A suggestion may also
                                    be referred to as a decision. Review the active filters and reporting period before comparing results.
                                </p>
                                <table class="metrics-table">
                                    <thead>
                                        <tr>
                                            <th style="width: 22%;">Metric</th>
                                            <th>Definition and calculation</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="metric in performanceMetrics" :key="metric.name">
                                            <td class="term">{{ metric.name }}</td>
                                            <td>
                                                <div>{{ metric.definition }}</div>
                                                <div class="formula q-mt-xs">{{ metric.formula }}</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="source-card">
                                <div class="source-title">Objective health</div>
                                <p class="bodydg-text">Objective health is based on Accept Rate and indicates the level of rep acceptance for a BCO.</p>
                                <table class="metrics-table">
                                    <thead>
                                        <tr>
                                            <th style="width: 22%;">Health</th>
                                            <th>Accept Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="health in objectiveHealth" :key="health.label">
                                            <td class="term">
                                                <i class="dot q-mr-xs" :style="`background: ${health.color};`"></i>{{ health.label }}
                                            </td>
                                            <td>{{ health.range }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="source-card">
                                <div class="source-title">How to read the metrics together</div>
                                <ul class="bullet-list">
                                    <li v-for="rule in metricRelationships" :key="rule">{{ rule }}</li>
                                </ul>
                                <div class="label">Example</div>
                                <p class="bodydg-text">
                                    For 100 suggestions, if 60 are accepted, 20 dismissed, 20 ignored, and 45 accepted suggestions are subsequently
                                    actioned, Engagement Rate is 80%, Accept Rate is 60%, Dismiss Rate is 20%, Ignore Rate is 20%, and Action Rate
                                    is 45%. Objective health is Average.
                                </p>
                            </div>

                            <div class="source-card">
                                <div class="source-title">Other key terms</div>
                                <table class="metrics-table">
                                    <thead>
                                        <tr>
                                            <th style="width: 22%;">Term</th>
                                            <th>Definition</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="term in otherTerms" :key="term.name">
                                            <td class="term">{{ term.name }}</td>
                                            <td>{{ term.definition }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </template>

                    <!-- AGENT KNOWLEDGE GUIDE -->
                    <template v-if="activeTab === 'agent-knowledge'">
                        <!-- DATA SOURCES -->
                        <section class="guide-section">
                            <div class="section-header">Data Sources</div>
                            <p class="bodydg-text">
                                To support NBA analytics, multiple data sources including prescribing data, patient claims data, promotional
                                activity data, Lab data, market access (MMIT) data, HCP profile data, call plan data, and micro-segmentation
                                datasets are considered to create a synthetic master dataset. A detailed overview of each dataset is provided below.
                            </p>

                            <div v-for="source in dataSources" :key="source.title" class="source-card">
                                <div class="source-title">{{ source.title }}</div>
                                <p v-if="source.description" class="bodydg-text">{{ source.description }}</p>

                                <template v-if="source.columns?.length">
                                    <div class="label">Key Columns</div>
                                    <ul class="bullet-list">
                                        <li v-for="column in source.columns" :key="column.name">
                                            <span class="term">{{ column.name }}</span><template v-if="column.description">: {{ column.description }}</template>
                                        </li>
                                    </ul>
                                </template>

                                <template v-if="source.usage">
                                    <div class="label">Usage</div>
                                    <p class="bodydg-text">{{ source.usage }}</p>
                                </template>

                                <template v-if="source.exampleVariables?.length">
                                    <div class="label">Example variables for the solution</div>
                                    <div class="chip-row">
                                        <div v-for="variable in source.exampleVariables" :key="variable" class="chip">{{ variable }}</div>
                                    </div>
                                </template>
                            </div>

                            <p class="bodydg-text">
                                The POC is focused on the US market, with Oncology as the therapy area and Scemblix as the brand of interest.
                                The analysis is designed to generate HCP-level recommendations, with each dataset providing a unique perspective
                                on prescribing behaviour, promotion responsiveness, patient opportunity and demographics attributes.
                            </p>
                        </section>

                        <!-- EXTERNAL DATA SOURCES -->
                        <section class="guide-section">
                            <div class="section-header">External Data Sources</div>
                            <p class="bodydg-text">
                                Relevant and validated external data sources have been identified to support the identification of new business
                                opportunities. The following sources are used as inputs for the analysis.
                            </p>

                            <div v-for="source in externalSources" :key="source.title" class="source-card">
                                <div class="source-title">{{ source.title }}</div>
                                <ul class="bullet-list">
                                    <li v-for="url in source.urls" :key="url">
                                        <span class="term">URL</span>:
                                        <a :href="url" target="_blank" rel="noopener noreferrer" class="link">{{ url }}</a>
                                    </li>
                                    <li><span class="term">Purpose</span>: {{ source.purpose }}</li>
                                    <li><span class="term">Key information from the source</span>: {{ source.keyInformation }}</li>
                                    <li><span class="term">Relevant fields to use from HCP 360</span>: {{ source.hcp360Fields }}</li>
                                </ul>
                            </div>
                        </section>

                        <!-- SEMANTIC LAYER -->
                        <section class="guide-section">
                            <div class="section-header">Semantic Layer</div>
                            <div class="label q-mb-xs">Description</div>
                            <p class="bodydg-text">
                                The NBA Semantic View is a Snowflake Semantic Layer built for the Novartis Next Best Action (NBA) Proof of Concept,
                                supporting the Scemblix brand. It provides a curated, business-friendly data model on top of underlying HCP 360 and
                                BCO data and is designed to serve three downstream consumers: the Strategize Agent (SA), the NBA generation &amp;
                                Explainability Agent (EA), and the Interactive Agent (IA).
                            </p>
                            <p class="bodydg-text">
                                <span class="term">Semantic View Name</span> – <span class="mono">NBA_POC_DB.PUBLIC.NBA_SEMANTIC_VIEW</span>
                            </p>

                            <div class="source-card">
                                <div class="source-title">Tables and Relationships</div>
                                <p class="bodydg-text">The semantic view is built on below core tables:</p>
                                <ul class="bullet-list">
                                    <li v-for="table in semanticTables" :key="table.name">
                                        <span class="term">{{ table.name }}</span> – {{ table.description }}
                                    </li>
                                </ul>
                            </div>

                            <div class="source-card erd-card">
                                <img src="/erd.png" alt="NBA Semantic Layer Entity Relationship Diagram" class="erd-image"/>
                            </div>

                            <div class="source-card">
                                <div class="source-title">Dimensions</div>
                                <p class="bodydg-text">The dimensions provide descriptive and categorical context for analysis, including:</p>
                                <ul class="bullet-list">
                                    <li v-for="dimension in dimensions" :key="dimension">{{ dimension }}</li>
                                </ul>
                            </div>

                            <div class="source-card">
                                <div class="source-title">Facts</div>
                                <p class="bodydg-text">The facts capture calculated, Recommendation tracking data and HCP-week-level performance and scoring measures, including:</p>
                                <ul class="bullet-list">
                                    <li v-for="fact in facts" :key="fact">{{ fact }}</li>
                                </ul>
                            </div>

                            <div class="source-card">
                                <div class="source-title">Metrics</div>
                                <p class="bodydg-text">
                                    The metrics are pre-defined aggregations built on top of the facts and dimensions, intended to support reporting
                                    and agent-level decisioning without requiring re-derivation of business logic. These include:
                                </p>
                                <table class="metrics-table">
                                    <thead>
                                        <tr>
                                            <th style="width: 40px;">No</th>
                                            <th style="width: 34%;">Metric Name</th>
                                            <th>Metric Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(metric, index) in metrics" :key="metric.name">
                                            <td>{{ index + 1 }}</td>
                                            <td class="term">{{ metric.name }}</td>
                                            <td>{{ metric.description }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </template>
                </div>
            </q-scroll-area>
        </q-card>
    </q-dialog>
</template>

<script>
const NPI_DESCRIPTION = "National Provider Identifier is a standard 10-digit ID for each doctor";

export default {
    name: "knowledge-guide",

    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["update:modelValue"],

    watch: {
        activeTab() {
            this.$refs.guideScroll?.setScrollPosition("vertical", 0);
        },
    },

    data() {
        return {
            activeTab: "user-guide",

            tabs: [
                { value: "user-guide", label: "Application User Guide" },
                { value: "agent-knowledge", label: "Agent Knowledge Guide" },
            ],

            thumbStyle: {
                borderRadius: "7px",
                backgroundColor: "#E5E8ED",
                width: "7px",
                opacity: 0.99,
            },

            walkthrough: [
                {
                    name: "NBA Opportunity Hub",
                    steps: [
                        {
                            number: 1,
                            image: "/step_01.png",
                            title: "Open the NBA Opportunity Hub",
                            description: "From the home screen, open NBA Opportunity Hub to explore business opportunities.",
                        },
                        {
                            number: 2,
                            image: "/step_02.png",
                            title: "Select a Strategic Imperative",
                            description: "Select a Strategic Imperative to view the associated BCOs.",
                        },
                        {
                            number: 3,
                            image: "/step_03.png",
                            title: "Select a BCO",
                            description: "Select an approved or agent-recommended BCO to review its feasibility and simulate its HCP cohort.",
                        },
                        {
                            number: 4,
                            image: "/step_04.png",
                            title: "Review, simulate, and approve the BCO cohort",
                            description: "Adjust the Lookback Period and click Submit (or Reset to default), use the trend icon to compare cohort sizes across periods, and tune the Cohort Simulator thresholds to update the cohort. Open NBA Copilot to explore the selected BCO and cohort, then click Review & Approve to approve the cohort and initiate NBA generation.",
                        },
                    ],
                },
                {
                    name: "NBA Agent Studio",
                    steps: [
                        {
                            number: 5,
                            image: "/step_05.png",
                            title: "Review recommendations and send for MLR approval",
                            description: "Review the Veeva Header & Message and sample recommendations for the new BCO, then select Send for MLR Approval.",
                        },
                        {
                            number: 6,
                            image: "/step_06.png",
                            title: "Start the BCO",
                            description: "Once the MLR status shows Approved, click Start to activate the BCO. Then open NBA Impact Center to monitor BCO performance.",
                        },
                    ],
                },
                {
                    name: "NBA Impact Center",
                    steps: [
                        {
                            number: 7,
                            image: "/step_07.png",
                            title: "Choose performance filters",
                            description: "Select BCOs individually or filter by Strategic Imperative, choose the required Month Range, and click Submit to load performance results for the selected filters.",
                        },
                        {
                            number: 8,
                            image: "/step_08.png",
                            title: "Review BCO performance",
                            description: "Review KPIs, objective health, dismissal reasons, channel mix, tier-wise and territory performance. Open NBA Copilot to explore BCO performance further.",
                        },
                    ],
                },
            ],

            generalInstructions: [
                {
                    title: "Session validity and demo preparation",
                    paragraphs: [
                        "A new session is created each time you log in and remains valid for <span class='term'>6 hours</span>. After 6 hours, the session is automatically invalidated and you will need to log in again.",
                        "Before a demo, log out and log in again to create a fresh session and reduce the risk of an unexpected logout during the demo.",
                    ],
                },
                {
                    title: "Refresh to view the latest UI",
                    paragraphs: [
                        "An open browser tab does not automatically load newly deployed UI updates. If a UI change is not visible, refresh the tab (<span class='kbd'>CTRL+SHIFT+R</span>) to fetch the latest version. This is especially relevant when a tab has remained open for a long time while updates were deployed.",
                    ],
                },
                {
                    title: "Refresh to view the latest shared data",
                    paragraphs: [
                        "Data and BCO status on Strategize and NBA Workbench are shared across all users. Refresh the page (<span class='kbd'>CTRL+SHIFT+R</span>) to fetch the latest data and status, including changes made by another user.",
                    ],
                },
            ],

            sharedChanges: [
                "Updating the cohort using the Cohort Simulator.",
                "Approving a BCO.",
                "Changing the MLR status.",
                "Starting a BCO.",
            ],

            acronyms: [
                { term: "NBA", meaning: "Next Best Action" },
                { term: "SI", meaning: "Strategic Imperative" },
                { term: "BCO", meaning: "Business Opportunity" },
                { term: "LOE", meaning: "Loss of Exclusivity" },
                { term: "HCP", meaning: "Healthcare Professional" },
                { term: "HCP 360", meaning: "Comprehensive HCP-level information covering prescribing behavior, promotion responsiveness, patient opportunity, and demographic attributes." },
                { term: "APLD", meaning: "Anonymous Patient-Level Data" },
                { term: "LOT", meaning: "Line of Therapy" },
                { term: "MLR", meaning: "Medical, Legal, and Regulatory" },
                { term: "pp", meaning: "Percentage points: the absolute difference between two percentages. For example, an increase from 60% to 70% is 10 pp." },
            ],

            bcoCategories: [
                { name: "Approved BCOs", definition: "Business opportunities already approved by the brand team and currently operating." },
                { name: "Agent recommended BCOs", definition: "New business opportunities generated by the agent based on external signals." },
            ],

            performanceMetrics: [
                {
                    name: "Engagement Rate",
                    definition: "Percentage of total suggestions for which the rep responded by accepting or dismissing.",
                    formula: "(Accepted + dismissed suggestions) / total suggestions × 100",
                },
                {
                    name: "Accept Rate",
                    definition: "Percentage of total suggestions accepted by reps.",
                    formula: "Accepted suggestions / total suggestions × 100",
                },
                {
                    name: "Dismiss Rate",
                    definition: "Percentage of total suggestions actively dismissed by reps.",
                    formula: "Dismissed suggestions / total suggestions × 100",
                },
                {
                    name: "Ignore Rate",
                    definition: "Percentage of total suggestions that received no response from reps.",
                    formula: "Ignored suggestions / total suggestions × 100",
                },
                {
                    name: "Action Rate",
                    definition: "Percentage of total suggestions accepted and subsequently actioned by reps.",
                    formula: "Accepted and actioned suggestions / total suggestions × 100",
                },
            ],

            objectiveHealth: [
                { label: "Good", range: "Above 80%", color: "#2e7d32" },
                { label: "Average", range: "50% to 80%, inclusive", color: "#f9a825" },
                { label: "At-Risk", range: "Below 50%", color: "#c62828" },
            ],

            metricRelationships: [
                "Engagement Rate = Accept Rate + Dismiss Rate, when calculated over the same reporting scope.",
                "Accept Rate + Dismiss Rate + Ignore Rate = 100%, when every suggestion is assigned to exactly one of these response categories. Display rounding may create a small difference.",
                "Action Rate cannot exceed Accept Rate when both use the same reporting scope.",
                "A Good health label reflects strong rep acceptance. It does not, by itself, demonstrate HCP reach, sales uplift, or ROI.",
            ],

            otherTerms: [
                { name: "Cohort", definition: "A group of HCPs included in a business opportunity based on the applicable selection criteria." },
                { name: "Suggestion", definition: "An NBA recommendation for an HCP. Also referred to as a decision in the underlying data or metric definitions." },
                { name: "Semantic layer", definition: "The business definitions and mappings used to interpret the application data and calculate metrics." },
            ],

            dataSources: [
                {
                    title: "Prescriptions Data",
                    description: "It captures the prescribing behaviour of HCPs for the brand and the competitor products. This data is sourced from IQVIA vendor at HCP x Week level and contains information about total prescriptions (TRx) and new prescriptions (NRx) written by different HCPs for different brands in the market.",
                    columns: [
                        { name: "HCP ID", description: "Unique HCP Identifier generated by vendor" },
                        { name: "NPI ID", description: NPI_DESCRIPTION },
                        { name: "Week Date", description: "Week date when the prescription written" },
                        { name: "Brand Name", description: "Name of the drug for which prescription is written" },
                        { name: "Therapy Class Name", description: "Name of the therapeutic class for which prescription is written" },
                        { name: "TRx", description: "Total number of prescriptions written" },
                        { name: "NRx", description: "Number of new prescriptions written" },
                    ],
                    usage: "This data helps in analysing the prescriptions patterns of the HCPs at brand level and based on that helps in identifying who are high and low value prescribers, what is the brand share in the market, what are the growth opportunities, adoption & switching trends, etc.",
                    exampleVariables: [
                        "HCP ID", "NPI ID", "Week", "Brand Rx", "Competitor Rx", "Brand Writer Flag",
                        "Brand Volume Segment", "Market Volume Segment", "Brand Share", "Brand Growth Rate (QoQ)",
                    ],
                },
                {
                    title: "Patient level Claims Data",
                    description: "It provides insights into the patient population managed by each HCP. This dataset is typically derived from longitudinal patient claims and treatment history data. Patient claims are sourced from IQVIA vendor which aggregate and anonymize the pharmacy and medical claims for analytics purposes. The granularity of these datasets is at HCP x Patient x Date level and contains information about prescriptions, diagnosis, and procedures claims.",
                    columns: [
                        { name: "Provider ID", description: "Unique HCP identifier generated by vendor" },
                        { name: "NPI ID", description: NPI_DESCRIPTION },
                        { name: "Rx Date", description: "Date when the prescription written" },
                        { name: "Patient ID", description: "Unique Patient identifier after anonymization" },
                        { name: "Claim ID", description: "Unique claim ID generated for the medical event" },
                        { name: "Claim Status", description: "Status of the claim whether it is approved, rejected, or reversed" },
                        { name: "Plan ID", description: "Unique identifier of the insurance plan through which patient is covered" },
                        { name: "Diagnosis Code", description: "ICD-9 or ICD-10 code signifying the medical condition/ disease diagnosed" },
                        { name: "Quantity Dispense", description: "Number of medications issued (example – 10 tablets, 2 vials)" },
                        { name: "Days of Supply", description: "Calculated duration of the medication (example – 30 days, 90 days)" },
                        { name: "Payer Type", description: "Categorization of the insurance coverage (example – Medicare, Medicaid, Commercial)" },
                        { name: "Source of Business", description: "Categorization of the patient encounter to find out where the business came from (example – new patient, switch, continue, restart)" },
                    ],
                    usage: "This data helps in analysing patient longitudinal journey and identifying HCPs with patients progressing through different lines of therapy and growth opportunities.",
                    exampleVariables: [
                        "Provider ID", "NPI ID", "Week", "Diagnosed Patients", "New Patients (NBRx)", "Switch Patients",
                        "Continue Patients", "LOT 1 Patients", "LOT 2 Patients", "Days of Supply",
                    ],
                },
                {
                    title: "CRM Promotion Data",
                    description: "It tracks promotional interactions between sales reps and HCPs. These are typically sourced from Veeva CRM system. The granularity of the data is at HCP x Date level and contains information about the number of calls and rep-triggered emails delivered by reps.",
                    columns: [
                        { name: "HCP ID", description: "Unique HCP Identifier generated by vendor" },
                        { name: "NPI ID", description: NPI_DESCRIPTION },
                        { name: "Date", description: "Date of the promotional interaction" },
                        { name: "Brand Name", description: "Name of the brand for which promotional interaction is sent" },
                        { name: "Rep Name", description: "Name of the sales rep who interacted with the HCP" },
                        { name: "Calls Suggestions", description: "Number of calls suggestions provided" },
                        { name: "Calls Detailed", description: "Number of calls delivered by the rep" },
                        { name: "RTE Sent", description: "Number of rep-triggered emails sent by rep" },
                        { name: "RTE Clicked", description: "Number of rep-triggered emails clicked by HCP" },
                        { name: "RTE Opened", description: "Number of rep-triggered emails opened by HCP" },
                    ],
                    usage: "This data helps in determining the promotional interactions of the reps with HCPs and promotional frequency, what is the adherence of the reps with respect to suggestions, what is the responsiveness of the HCPs for the rep-triggered emails.",
                    exampleVariables: [
                        "HCP ID", "NPI ID", "Week", "Calls Suggestions", "Calls Delivered", "Call Engagement Rate",
                        "RTE Sent", "RTE Clicked", "RTE Opened", "RTE Engagement Rate", "Days since last call", "Days since last RTE",
                    ],
                },
                {
                    title: "Digital Promotion Data",
                    description: "It captures promotional interactions of HCPs with digital channels such as 3rd Party Emails (TPE), HQ Emails, and Banner Ads. Primary vendors for such type of digital engagements are SFMC, IQVIA, Medscape, Pulse Point, Biopharma and DMD. The granularity of these datasets is at HCP x Date level.",
                    columns: [
                        { name: "HCP ID", description: "Unique HCP Identifier generated by vendor" },
                        { name: "NPI ID", description: NPI_DESCRIPTION },
                        { name: "Date", description: "Date of the promotional interaction" },
                        { name: "Brand Name", description: "Name of the brand for which promotional interaction is sent" },
                        { name: "Vendor Name", description: "Name of the vendor who sent the digital promotion" },
                        { name: "3RD Party Emails Sent", description: "Number of TPE sent" },
                        { name: "3RD Party Emails Clicked", description: "Number of TPE clicked" },
                        { name: "3RD Party Emails Opened", description: "Number of TPE opened" },
                        { name: "HQ Emails Sent", description: "Number of HQ emails sent" },
                        { name: "HQ Emails Clicked", description: "Number of HQ emails clicked" },
                        { name: "HQ Emails Opened", description: "Number of HQ emails opened" },
                        { name: "Banner Ads Sent", description: "Number of banner ads sent" },
                        { name: "Banner Ads Clicked", description: "Number of banner ads clicked" },
                        { name: "Banner Ads Opened", description: "Number of banner ads opened" },
                    ],
                    usage: "This data helps in identifying digitally engaged HCPs and their preferred communication channel and engagement opportunities.",
                    exampleVariables: [
                        "HCP ID", "NPI ID", "Week", "SFMC TPE Sent", "SFMC TPE Clicked", "SFMC TPE Opened",
                        "Banner Ads Sent", "Banner Ads Clicked",
                    ],
                },
                {
                    title: "HCP Profile",
                    description: "It provides various HCP level attributes such as their name, address, ZIP code, specialty, practice type, affiliation type, years in practice, etc.",
                    columns: [
                        { name: "HCP ID" },
                        { name: "NPI ID" },
                        { name: "HCP Name" },
                        { name: "HCP Address" },
                        { name: "HCP City" },
                        { name: "HCP State" },
                        { name: "HCP ZIP" },
                        { name: "HCP Specialty" },
                        { name: "HCP Affiliation" },
                    ],
                    usage: "This data helps in mapping various attributes of the HCPs to derive any static segmentation variable.",
                },
                {
                    title: "Market Access Data",
                    description: "It provides payer and reimbursement information affecting product accessibility. It is typically sourced from MMIT, payer, or formulary access datasets.",
                    columns: [
                        { name: "Plan ID", description: "Unique identifier for the insurance plan" },
                        { name: "Plan Name", description: "Name of the insurance plan" },
                        { name: "Product Name", description: "Name of the brand associated with the insurance plan" },
                        { name: "Formulary Status", description: "Status of the insurance plan such as covered, not covered, covered with prior authorization, etc." },
                        { name: "Lives Covered", description: "Number of lives covered under each plan" },
                    ],
                },
                {
                    title: "LAB Data (MMIT / Diaceutics)",
                    description: "It contains information about new and suspected diagnosed patients at HCP ID x Date level.",
                    columns: [
                        { name: "NPI ID", description: NPI_DESCRIPTION },
                        { name: "Date", description: "Date of the lab test" },
                        { name: "Trigger ID", description: "Code generated based on lab test such as A for new diagnosed and J for suspected diagnosed" },
                        { name: "N Patients", description: "Numbers of patients associated with the test" },
                    ],
                },
                {
                    title: "Target List",
                    description: "It contains the list of HCPs which are part of brand target list. The list is generally created based on the prescription history of the HCP in the market.",
                },
                {
                    title: "Call Plan",
                    description: "It contains the planned number of HCP-level calls for each cycle and outlines which HCPs should be contacted, how frequently they should be contacted, and by which field force during a planning period (quarter, cycle, or year). In NBA process, call plan will be used to focus recommendations on high-priority HCPs.",
                },
                {
                    title: "Micro-segmentation",
                    description: "It contains information about various segments derived at HCP level based on their behavioural attributes.",
                },
                {
                    title: "Rep Roaster",
                    description: "It contains information about the sales rep such as rep name, territory name, if rep is on vacation, if the associated territory is vacant.",
                },
                {
                    title: "ZIP-Territory Alignment",
                    description: "It provides information about territory and ZIPs assigned for sales rep.",
                },
            ],

            externalSources: [
                {
                    title: "Approved Drug Products with Therapeutic Equivalence Evaluations (commonly known as the Orange Book)",
                    urls: [
                        "https://www.fda.gov/drugs/drug-approvals-and-databases/approved-drug-products-therapeutic-equivalence-evaluations-orange-book",
                    ],
                    purpose: "Identify competitors which are approaching loss of exclusivity and combine the information with prescribing behaviour to create actionable conversion opportunities.",
                    keyInformation: "Competitors names along with their patent expiration date.",
                    hcp360Fields: "Brand and competitors’ prescription behaviour, CML Diagnosed population.",
                },
                {
                    title: "Scemblix Coverage Notification",
                    urls: [
                        "https://www.uhcprovider.com/en/prior-auth-advance-notification/prior-auth-specialty-drugs/prior-auth-pharmacy-medical-necessity.html",
                        "https://info.caremark.com/content/dam/enterprise/caremark/microsites/dig/pdfs/pa-fep/fep-criteria/FEP_Criteria_Scemblix.pdf",
                    ],
                    purpose: "Identify signals of brand access improvement to generate growth opportunity.",
                    keyInformation: "Latest coverage criteria for Scemblix.",
                    hcp360Fields: "Historical Scemblix prescribing trend, Scemblix formulary status, CML patient volume.",
                },
                {
                    title: "Patient Advocacy & Awareness Organizations",
                    urls: [
                        "https://bloodcancerunited.org/blood-cancer/leukemia/chronic-myeloid-leukemia-cml",
                        "https://www.nationalcmlsociety.org/",
                    ],
                    purpose: "Identify recent patient-focused activities such as awareness campaigns, educational initiatives, support programs, or advocacy efforts. Treat these activities as potential Patient Activation triggers.",
                    keyInformation: "Latest patient focussed activities for Scemblix.",
                    hcp360Fields: "CML patient volume, New/Suspected Diagnosed Patient Count.",
                },
            ],

            semanticTables: [
                { name: "SI_BCO_MAPPING", description: "A reference mapping table containing the relationships between Strategic Imperatives (SIs) and Business Contextual Objectives (BCOs), including BCO category, business rules, opportunity type, and associated Veeva messaging content. The agent reads this table to identify and evaluate relevant BCO opportunities." },
                { name: "HCP_360", description: "A weekly, HCP-level analytical table providing a consolidated view of commercial and prescribing activity for the Scemblix NBA program. It includes prescribing trends, patient mix, engagement history, HCP attributes, and previous next-best-action outputs. The agent reads this table to identify HCP-level signals and inform recommendation decisions." },
                { name: "HCP_REP_MAPPING", description: "An HCP-to-representative mapping table containing rep name, propensity score, territory information, and call and RTE capacity. It is linked to HCP_360 and BCO_Combination tables through common identifiers such as HCP_ID. The agent reads this table to associate eligible HCPs with the appropriate representatives and assess available engagement capacity." },
                { name: "BCO_MASTER", description: "The master reference table containing the definition and configuration of each BCO, including its objective, governing business rules, recommendation logic, and current status. The agent writes newly generated BCOs to this table and reads existing records in subsequent cycles so that prior recommendations can inform future decisions." },
                { name: "BCO_HCP_MAPPING", description: "A BCO-to-HCP cohort mapping table containing the HCPs included in the final eligible cohort for each BCO. Each record associates a BCO_ID with an HCP_ID. The agent writes the selected cohorts to this table and reads previous mappings in subsequent cycles to incorporate historical cohort selections into future recommendations." },
                { name: "BCO_COMBINATION_MAPPING", description: "A recommendation-output table containing the selected BCO combinations for each applicable HCP, representative, and engagement channel. It also includes the associated messaging and explainability content and links each combination to the relevant record in BCO_MASTER. This is a write-only table for the agent and stores the final recommendation combinations generated during each cycle." },
                { name: "COMMAND_CENTRE_DATA", description: "A recommendation-performance table containing representative-response measures and KPIs used to assess the adoption, execution, and effectiveness of generated recommendations. The agent reads this table to incorporate historical response and performance outcomes into future recommendation cycles." },
                { name: "REP_FEEDBACK_DATA", description: "A table containing the feedback provided by the reps on the generated recommendations. It includes feedback status (Accepted, Dismissed, Ignored) along with the reasons. It is linked to BCO_Combination table through HCP_ID and Rep_ID. The agent reads this table to identify and block any recommendation based on the feedback provided." },
            ],

            dimensions: [
                "HCP identity and demographics (HCP name, NPI, address, city, state, specialty, years in practice)",
                "A time dimension, WEEK_END_DATE, marking the week-ending date for each HCP-week record",
                "HCP segmentation (adoption stage, behavioural segment, attitudinal segment, opportunity segment, KOL tier, priority segment, brand-writing segment)",
                "Prescribing and patient volume indicators (brand/competitor Rx counts, patient counts by line of therapy, new/discontinued/switched patient counts)",
                "Promotional channel and engagement activity (call counts, RTE sends/opens/clicks, banner ads, HQ and third-party emails, suggestion status)",
                "Rep and territory attributes (rep name, rep ID, territory, capacity, active flags, dismissal reasons)",
                "BCO master attributes (BCO type, rule, recommendation source/type, strategic imperative, status)",
                "Strategic Imperative–to-BCO mapping attributes (BCO category, business rule, opportunity type, SI category, Veeva header/reason text)",
                "Command Centre metrics used for command centre KPI, attributes like (accept/dismiss/ignore/engage flags, dismissal reason, HCP-reached flag, recommended channel, suggestion ID)",
            ],

            facts: [
                "Prescribing performance (brand share, brand Rx growth, competitor Rx growth)",
                "Patient access indicators (out-of-pocket cost, covered lives %, prior authorization %, step edit %)",
                "Engagement rates (call engagement rate, RTE engagement rate) at both HCP and territory level, over trailing 4- and 13-week windows",
                "Composite scores (Rx momentum score, promotional responsiveness score, rep propensity score, patient opportunity score, payer access score, call plan execution score)",
            ],

            metrics: [
                { name: "Objectives Running", description: "Total active objectives currently being tracked in the Command Centre" },
                { name: "Suggestions This Cycle", description: "Total recommendations generated during the cycle" },
                { name: "Engagement Rate", description: "Percentage of suggestions that received rep engagement" },
                { name: "% Reached HCPs", description: "Percentage of HCPs successfully contacted" },
                { name: "Accept Rate", description: "Percentage of suggestions accepted by reps" },
                { name: "Objective Suggestion Count", description: "Total suggestions generated for a specific BCO" },
                { name: "Objective Accept Rate", description: "Accept rate for a specific BCO" },
                { name: "Total Accepted Suggestions", description: "Total accepted recommendations" },
                { name: "Total Dismissed Suggestions", description: "Total dismissed recommendations" },
                { name: "Total Ignored Suggestions", description: "Total ignored recommendations" },
                { name: "RTE Channel Mix %", description: "Share of recommendations delivered through RTE" },
                { name: "Call Channel Mix %", description: "Share of recommendations delivered through Calls" },
                { name: "Tier HCP Count", description: "Number of HCPs in each Brand Tier" },
                { name: "Tier Engagement Rate", description: "Engagement rate by Brand Tier" },
                { name: "Rep Dismissal Reason - Recently Visited %", description: "Share of dismissals due to recent visit" },
                { name: "Rep Dismissal Reason - No Access to HCP %", description: "Share of dismissals due to access issues" },
                { name: "Rep Dismissal Reason - Content Not Relevant %", description: "Share of dismissals due to content relevance" },
                { name: "Rep Dismissal Reason - Not My Target %", description: "Share of dismissals due to targeting issues" },
                { name: "Rep Dismissal Reason - Timing/Cadence %", description: "Share of dismissals due to timing concerns" },
                { name: "Territory Suggestions", description: "Total suggestions generated within a territory" },
                { name: "Territory Reached HCPs", description: "Unique HCPs reached within a territory" },
                { name: "Territory Engagement Rate", description: "Engagement rate within a territory" },
                { name: "Territory Accept Rate", description: "Accept rate within a territory" },
            ],
        };
    },
};
</script>

<style scoped>
.guide {
    font-size: 12px;
    line-height: 18px;
    color: #3f3f3f;
}

.tabs {
    color: #5f5f5f;
    font-size: 12px;
    font-weight: 600;
}

.guide-section + .guide-section {
    margin-top: 28px;
}

.section-header {
    font-size: 15px;
    font-weight: 700;
    color: #080808;
    padding-bottom: 6px;
    margin-bottom: 10px;
    border-bottom: 2px solid #ff4e00;
}

.sub-header {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #ff4e00;
    margin-bottom: 8px;
}

.source-card {
    padding: 10px 12px;
    margin-bottom: 10px;
    background: #fafafa;
    border: 1px solid #ededed;
    border-radius: 6px;
}

.source-title {
    font-size: 12px;
    font-weight: 600;
    color: #080808;
    margin-bottom: 4px;
}

.bodydg-text {
    margin: 0 0 8px 0;
}

.bodydg-text:last-child {
    margin-bottom: 0;
}

.label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #5f5f5f;
    margin-bottom: 4px;
}

.bullet-list {
    margin: 0 0 8px 0;
    padding-left: 18px;
}

.bullet-list li {
    margin-bottom: 3px;
}

.bullet-list li::marker {
    color: #ff4e00;
}

.term,
.guide :deep(.term) {
    font-weight: 600;
    color: #080808;
}

.tab-link {
    cursor: pointer;
    color: #b33700;
    text-decoration: underline;
}

.tab-link:hover {
    color: #ff4e00;
}

.formula,
.mono,
.guide :deep(.kbd) {
    display: inline-block;
    font-family: "Courier New", monospace;
    font-size: 11px;
    background: #f0f0f0;
    border-radius: 4px;
    padding: 1px 5px;
}

.link {
    color: #b33700;
    text-decoration: underline;
    overflow-wrap: anywhere;
}

.link:hover {
    color: #ff4e00;
}

.chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.chip {
    padding: 2px 8px;
    background: #f0f0f0;
    border: 1px solid #dadada;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 600;
    color: #5f5f5f;
}

.dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
}

.metrics-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    line-height: 16px;
}

.metrics-table th {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #5f5f5f;
    background: #f0f0f0;
    padding: 6px 8px;
    border: 1px solid #e2e2e2;
}

.metrics-table td {
    padding: 5px 8px;
    border: 1px solid #ededed;
    vertical-align: top;
    background: #ffffff;
}

.screenshot {
    display: block;
    width: 100%;
    height: auto;
    margin-top: 4px;
    border: 1px solid #e2e2e2;
    border-radius: 4px;
    cursor: zoom-in;
}

.erd-card {
    padding: 8px;
    text-align: center;
}

.erd-image {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
}
</style>
